/**
 * 多域名 / 线路切换工具(PC 端)。
 *
 * 线路池来自构建时从 nginx 提取的 window.__API_LINES__(见 build/gen-api-lines.js)。
 * 作用于:
 *   - 普通接口请求(httpRequest.js 拦截器动态读取 baseURL)
 *   - WebSocket 连接(由当前线路派生 wss://api.x/im-server/im)
 * 与后台管理 manager/src/utils/apiLine.js 规则一致。
 */

const STORE_KEY = 'pc_api_line' // 手动选中的线路 url;不存在表示「自动」

// 编译期默认 baseURL(仅当线路池为空时兜底)
const ENV_BASE = process.env.VUE_APP_BASE_API

// WebSocket 后端路径(与 nginx api 块的 location /im-server/im 一致)
const WS_PATH = '/im-server/im'

// 运行时实际生效的线路(自动模式测速后写入内存,避免污染 localStorage)
let activeLine = null

/** 线路池(已去重),来自构建注入的 window.__API_LINES__ */
export function getApiLines() {
  const list = (typeof window !== 'undefined' && window.__API_LINES__) || []
  return Array.from(new Set(list.filter(Boolean)))
}

/** 是否启用线路切换:存在候选线路才有意义 */
export function lineSwitchEnabled() {
  return getApiLines().length > 0
}

/**
 * 当前访问域名对应的 api 线路。
 * 模板: pcim.qiyuan168.top -> https://api.qiyuan168.top
 * 线上: htpcim.wentyi.com / htim.wentyi.com / htweb.wentyi.com -> https://htapi.wentyi.com
 * localhost/IP 下返回 null。
 */
function currentHostApi() {
  try {
    if (typeof window === 'undefined') return null
    const host = window.location.hostname || ''
    // localhost / 纯 IP 不做推导(走线路池或兜底)
    if (!host || host === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null
    const root = host.replace(/^[^.]+\./, '') // 去掉第一段子域
    if (!root || root.indexOf('.') === -1) return null
    // 线上子域以 ht 开头(htpcim/htim/htweb)时走 htapi，否则走 api
    const sub = host.split('.')[0] || ''
    const apiPrefix = /^ht/.test(sub) ? 'htapi.' : 'api.'
    return 'https://' + apiPrefix + root
  } catch (e) {
    return null
  }
}

/**
 * 默认 baseURL,优先级:
 * 当前访问域名同根的 api 线路(自动适配多域名) > 线路池第一条 > 编译期 VUE_APP_BASE_API。
 * 这样从任意 pcim.<域名> 打开,接口/WS 都自动走对应的 api.<域名>,不会串到别的域名。
 */
export function getDefaultBase() {
  const sameRoot = currentHostApi()
  if (sameRoot) return sameRoot
  const lines = getApiLines()
  return lines.length ? lines[0] : ENV_BASE
}

/** 默认 baseURL 是否为绝对地址(http/https) */
export function isAbsoluteBase() {
  return /^https?:\/\//i.test(getDefaultBase() || '')
}

/**
 * 当前生效的 baseURL,优先级:
 * 运行时 activeLine(自动选中的最优 / 手动选中) > localStorage 持久化的手动选择 > 默认 baseURL
 */
export function getCurrentLine() {
  const lines = getApiLines()
  if (activeLine && lines.includes(activeLine)) return activeLine
  try {
    const saved = localStorage.getItem(STORE_KEY)
    if (saved && lines.includes(saved)) return saved
  } catch (e) {
    /* ignore */
  }
  return getDefaultBase()
}

/** 由 http(s) 线路派生 WebSocket 地址:https://api.x -> wss://api.x/im-server/im */
export function toWsUrl(base) {
  const url = base || getDefaultBase() || ''
  return url.replace(/^https:\/\//i, 'wss://').replace(/^http:\/\//i, 'ws://') + WS_PATH
}

/** 当前生效线路对应的 WebSocket 地址 */
export function getCurrentWsUrl() {
  return toWsUrl(getCurrentLine())
}

/** 设为运行时生效线路(不持久化),供自动模式测速后调用 */
export function applyLine(url) {
  activeLine = url || null
}

/** 持久化手动选择;传空 = 恢复自动(清除持久化) */
export function setCurrentLine(url) {
  try {
    if (url) localStorage.setItem(STORE_KEY, url)
    else localStorage.removeItem(STORE_KEY)
  } catch (e) {
    /* ignore */
  }
}

/** 用户是否手动选过线路(即非自动模式) */
export function isManualSelected() {
  try {
    const saved = localStorage.getItem(STORE_KEY)
    return !!saved && getApiLines().includes(saved)
  } catch (e) {
    return false
  }
}

// 测速探针:用存在的图像验证码接口(/captcha),GET、无需登录、CORS 已放开、网关白名单放行。
// 不能用不存在的接口(会 404),否则会触发网关的高频 404 自动封禁,把自己测速的 IP 封掉。
// 这是 HTTP 请求测往返时间(RTT),不是 ICMP ping,服务器禁 ping 不影响。
const PROBE_PATH = '/captcha'

/**
 * 测单条线路延迟(RTT, ms)。fetch GET 探测公开接口:
 * - 拿到响应(含 HTTP 4xx/5xx)即视为「可达」,返回往返耗时;
 * - 网络不通 / CORS 未放开 / 超时,返回 null(判为不可达)。
 */
export function pingLine(base, timeout = 6000) {
  return new Promise((resolve) => {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeout)
    const start = performance.now()
    fetch(base + PROBE_PATH + '?_ping=' + Date.now(), {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal
    })
      .then(() => {
        clearTimeout(timer)
        resolve(Math.round(performance.now() - start))
      })
      .catch(() => {
        clearTimeout(timer)
        resolve(null)
      })
  })
}

/** 并发测速所有线路,返回 [{ url, rtt }],rtt 为 null 表示不可达 */
export function pingAll(lines = getApiLines()) {
  return Promise.all(
    lines.map(async (url) => ({ url, rtt: await pingLine(url) }))
  )
}

/** 从测速结果挑最优节点(rtt 最小且可达),无则返回 null */
export function pickBest(results) {
  const ok = results
    .filter((r) => r && r.rtt != null)
    .sort((a, b) => a.rtt - b.rtt)
  return ok.length ? ok[0].url : null
}
